import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";

interface IFileUploadProps {
    componentId: string;
    onCompleteOperation?: number;
    onErrorOperation?: number;
    onFileSelectionCallback: number;
    className?: string;

    /**
     * Whether to allow uploading multiple files at once.
     */
    allowMultiple?: boolean;

    onCompleted?: () => void;
    onError?: (errors: ErrorMessage[]) => void;
}

class FileUploadState {
    public files: File[];
}

class FileUpload extends BaseReactComponent<IFileUploadProps, FileUploadState> {
    private dispatchReference: string;
    private uploadComponent: HTMLInputElement;

    /**
     * Whether the user has any files selected.
     */
    public get hasFiles(): boolean {
        return this.state.files.any();
    }

    /**
     * Whether the user can upload multiple files at once.
     */
    public get allowMultiple(): boolean {
        return this.props.allowMultiple ?? true;
    }

    constructor(props: IFileUploadProps) {
        super(props);

        this.state = {
            files: []
        };
    }

    /**
     * Open the file upload UI.
     */
    public openUpload(): void {
        this.uploadComponent?.click();
    }

    public render(): JSX.Element {
        return (
            <div style={{ display: "flex" }} className={this.props.className}>
                <div style={{ display: "none" }}>
                    <input
                        ref={ref => this.uploadComponent = ref}
                        id={this.props.componentId + "_uploadinput"}
                        data-cy="file-upload-input"
                        name="files"
                        type="file"
                        multiple={this.allowMultiple}
                    />
                </div>
                <div>
                    {this.state.files.map((file, i) => (
                        <div key={`${file.name}_${i}`} className="flex-container" style={{ justifyContent: "space-between" }}>
                            <div style={{ marginLeft: "0px" }}>{file.name}</div>
                            <div style={{ marginLeft: "5px" }}>
                                <a style={{ color: "red", fontWeight: "bold" }} onClick={() => this.removeFile(file)} data-cy="file-upload-remove">X</a>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    public componentDidMount(): void {
        this.initDispatchSubscription();

        this.uploadComponent.addEventListener("change", () => { this.onFileChange(); });
    }

    private onFileChange(): void {
        let files = Array.from(this.uploadComponent.files);
        const newFilesSize = files.reduce((sum, current) => sum + current.size, 0);
        const currentFilesSize = this.state.files.reduce((sum, current) => sum + current.size, 0);
        if (newFilesSize + currentFilesSize > cacheStore.BatchScanFileMaximum) {
            sessionStore.showPopup({
                title: cacheStore.GetString("ErrorLabel"),
                content: [`${cacheStore.GetString("ExceedTheMaximumSizeAllowedLabel")} (${(cacheStore.BatchScanFileMaximum / 1000000)}MB)`],
                autoWidth: true
            });

            return;
        }

        files = files.map(file => {
            const newName = file.name.replace(/[&+%]/g, "");
            if (newName === file.name)
                return file;

            let blob = file.slice(0, file.size, file.type);
            return new File([blob], newName, { type: file.type });
        });

        this.addFiles(files);

        this.clearInput(); // avoid issue where you can't reupload a file after removing it since the input didn't change
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        this.uploadComponent.removeEventListener("change", null);

        MiscDispatcher.unregister(this.dispatchReference);
    }

    private initDispatchSubscription(): void {
        this.dispatchReference = MiscDispatcher.register((request: LocalEvent) => {
            switch (request.Operation) {
                case LocalOperations.Misc.FileUploader_Send:
                    {
                        const fileDescription = request.Get<object>("fileDescription");
                        this.uploadFiles(fileDescription, "api/medicalDocuments/upload");
                    }
                    break;

                case LocalOperations.Misc.FileUploader_Update:
                    {
                        const fileDescription = request.Get<object>("fileDescription");
                        this.uploadFiles(fileDescription, "api/medicalDocuments/updateunapprouveddocumentmetadata");
                    }
                    break;
            }
        });
    }

    /**
     * Remove a file from the list to upload.
     * @param file The file instance.
     */
    private removeFile(file: File): void {
        this.mutateState(
            s => s.files = s.files.filter(f => f !== file),
            () => sessionStore.dispatch(this.props.onFileSelectionCallback, { count: this.state.files.length, files: this.state.files })
        );
    }

    /**
     * Add a set of files to the list to upload.
     * @param files The file instances.
     */
    private addFiles(files: File[]): void {
        this.mutateState(
            s => {
                if (this.allowMultiple)
                    s.files = [...s.files, ...files];
                else
                    s.files = [files[files.length - 1]];
            },
            () => sessionStore.dispatch(this.props.onFileSelectionCallback, { count: this.state.files.length, files: this.state.files })
        );
    }

    /**
     * Remove all files and reset the HTML input control.
     */
    private clearFiles(): void {
        this.clearInput();
        this.mutateState(s => s.files = []);
    }

    /**
     * Reset the HTML input control.
     */
    private clearInput(): void {
        if (this.uploadComponent) {
            this.uploadComponent.value = "";
        }
    }

    /**
     * Upload the current files to the server.
     * @param fileDescription The file metadata object.
     * @param route The API endpoint route to submit files to, excluding the base URL.
     */
    private async uploadFiles(fileDescription: object, route: string): Promise<void> {
        if (!this.state.files.any())
            return;

        const response = await Client.postFiles<any>(route, this.state.files, { fileDescription: fileDescription });
        if (response.isSuccess) {
            this.clearFiles();

            if (this.props.onCompleted)
                this.props.onCompleted();
            else
                sessionStore.dispatch(this.props.onCompleteOperation, null, 50);
        }
        else {
            if (this.props.onError)
                this.props.onError(response.errors);
            else {
                if (response.errors.any())
                    sessionStore.showPopup(response.errors);
                sessionStore.dispatch(this.props.onErrorOperation, null, 50);
            }
        }
    }
}

export { FileUpload };
